import {
  collection,
  query,
  where,
  orderBy,
  startAfter,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  writeBatch,
  QueryDocumentSnapshot,
  DocumentData,
  limit,
} from 'firebase/firestore';
import { getDb } from './firebase';
import { generateQueryKeywords } from '@2bforest/common/utils';
import type { ForestryOperation, ForestType, OperationType, InterventionType } from '../types';
import type { ForestryImportData } from '../utils/excelImport';

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export interface ListResult<T> {
  items: T[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}

// ---------------------------------------------------------------------------
// ForestryOperation — legacy interface used in SuppliersPage multi-select
// ---------------------------------------------------------------------------

/** Minimal shape returned by getActiveOperations (used by SuppliersPage). */
export interface ActiveOperation {
  id: string;
  description: string;
}

/**
 * Returns all active forestry operations, ordered by description.
 * Used to populate the services offered multi-select in supplier management.
 */
export async function getActiveOperations(): Promise<ActiveOperation[]> {
  const q = query(
    collection(getDb(), 'forestry_operations'),
    where('isActive', '==', true),
    orderBy('description')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({
    id: d.id,
    description: d.data().description as string,
  }));
}

// ---------------------------------------------------------------------------
// ForestryOperation — full CRUD
// ---------------------------------------------------------------------------

const FORESTRY_OPS = 'forestry_operations';

function toForestryOperation(snap: QueryDocumentSnapshot<DocumentData>): ForestryOperation {
  const d = snap.data();
  return {
    id: snap.id,
    description: d.description ?? '',
    linkedInterventionTypeIds: d.linkedInterventionTypeIds ?? [],
    linkedOperationTypeIds: d.linkedOperationTypeIds ?? [],
    linkedForestTypeIds: d.linkedForestTypeIds ?? [],
    minValue: d.minValue ?? 0,
    avgValue: d.avgValue ?? 0,
    maxValue: d.maxValue ?? 0,
    pricePerHour: d.pricePerHour ?? null,
    fixedPrice: d.fixedPrice ?? null,
    unit: d.unit ?? '',
    totalUnits: d.totalUnits ?? null,
    avgTotalCost: d.avgTotalCost ?? null,
    minValueConditions: d.minValueConditions ?? '',
    maxValueConditions: d.maxValueConditions ?? '',
    source: d.source ?? null,
    isActive: d.isActive ?? true,
    searchKeywords: d.searchKeywords ?? [],
    createdAt: d.createdAt,
    updatedAt: d.updatedAt,
  };
}

export interface ListForestryOperationsParams {
  pageSize?: number;
  lastDoc?: QueryDocumentSnapshot<DocumentData> | null;
  searchQuery?: string;
  forestTypeId?: string;
  operationTypeId?: string;
  isActive?: boolean | null;
}

export async function listForestryOperations({
  pageSize = 50,
  lastDoc: cursor = null,
  searchQuery = '',
  forestTypeId = '',
  operationTypeId = '',
  isActive = null,
}: ListForestryOperationsParams = {}): Promise<ListResult<ForestryOperation>> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const constraints: any[] = [];

  // Apply array-contains filter — Firestore allows only one at a time
  if (forestTypeId) {
    constraints.push(where('linkedForestTypeIds', 'array-contains', forestTypeId));
  } else if (operationTypeId) {
    constraints.push(where('linkedOperationTypeIds', 'array-contains', operationTypeId));
  }

  if (isActive !== null) {
    constraints.push(where('isActive', '==', isActive));
  }

  if (searchQuery) {
    constraints.push(
      where('searchKeywords', 'array-contains-any', generateQueryKeywords(searchQuery))
    );
  }

  constraints.push(orderBy('description', 'asc'));
  if (cursor) constraints.push(startAfter(cursor));
  constraints.push(limit(pageSize + 1));

  const q = query(collection(getDb(), FORESTRY_OPS), ...constraints);
  const snapshot = await getDocs(q);

  const docs = snapshot.docs;
  const hasMore = docs.length > pageSize;
  const pageDocs = hasMore ? docs.slice(0, pageSize) : docs;

  return {
    items: pageDocs.map(toForestryOperation),
    lastDoc: pageDocs.length > 0 ? pageDocs[pageDocs.length - 1] : null,
    hasMore,
  };
}

export async function createForestryOperation(
  data: Omit<ForestryOperation, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  const now = new Date().toISOString();
  const ref = await addDoc(collection(getDb(), FORESTRY_OPS), {
    ...data,
    createdAt: now,
    updatedAt: now,
  });
  return ref.id;
}

export async function updateForestryOperation(
  id: string,
  data: Partial<Omit<ForestryOperation, 'id' | 'createdAt'>>
): Promise<void> {
  await updateDoc(doc(getDb(), FORESTRY_OPS, id), { ...data, updatedAt: new Date().toISOString() });
}

export async function deleteForestryOperation(id: string): Promise<void> {
  await deleteDoc(doc(getDb(), FORESTRY_OPS, id));
}

// ---------------------------------------------------------------------------
// Forest Types
// ---------------------------------------------------------------------------

const FOREST_TYPES = 'forestry_forest_types';

function toForestType(snap: QueryDocumentSnapshot<DocumentData>): ForestType {
  const d = snap.data();
  return { id: snap.id, name: d.name ?? '', order: d.order ?? 0 };
}

export async function listForestTypes(): Promise<ForestType[]> {
  const snapshot = await getDocs(query(collection(getDb(), FOREST_TYPES), orderBy('order', 'asc')));
  return snapshot.docs.map(toForestType);
}

export async function createForestType(data: Omit<ForestType, 'id'>): Promise<string> {
  const ref = await addDoc(collection(getDb(), FOREST_TYPES), data);
  return ref.id;
}

export async function updateForestType(
  id: string,
  data: Partial<Omit<ForestType, 'id'>>
): Promise<void> {
  await updateDoc(doc(getDb(), FOREST_TYPES, id), data);
}

export async function deleteForestType(id: string): Promise<void> {
  await deleteDoc(doc(getDb(), FOREST_TYPES, id));
}

// ---------------------------------------------------------------------------
// Operation Types
// ---------------------------------------------------------------------------

const OPERATION_TYPES = 'forestry_operation_types';

function toOperationType(
  snap: QueryDocumentSnapshot<DocumentData>
): OperationType & { linkedForestTypeIds?: string[] } {
  const d = snap.data();
  return {
    id: snap.id,
    name: d.name ?? '',
    order: d.order ?? 0,
    linkedForestTypeIds: d.linkedForestTypeIds ?? [],
  };
}

export async function listOperationTypes(): Promise<
  (OperationType & { linkedForestTypeIds?: string[] })[]
> {
  const snapshot = await getDocs(
    query(collection(getDb(), OPERATION_TYPES), orderBy('order', 'asc'))
  );
  return snapshot.docs.map(toOperationType);
}

export async function createOperationType(data: Omit<OperationType, 'id'>): Promise<string> {
  const ref = await addDoc(collection(getDb(), OPERATION_TYPES), data);
  return ref.id;
}

export async function updateOperationType(
  id: string,
  data: Partial<Omit<OperationType, 'id'>>
): Promise<void> {
  await updateDoc(doc(getDb(), OPERATION_TYPES, id), data);
}

export async function deleteOperationType(id: string): Promise<void> {
  await deleteDoc(doc(getDb(), OPERATION_TYPES, id));
}

// ---------------------------------------------------------------------------
// Intervention Types
// ---------------------------------------------------------------------------

const INTERVENTION_TYPES = 'forestry_intervention_types';

function toInterventionType(
  snap: QueryDocumentSnapshot<DocumentData>
): InterventionType & { linkedForestTypeIds?: string[] } {
  const d = snap.data();
  return {
    id: snap.id,
    name: d.name ?? '',
    order: d.order ?? 0,
    linkedOperationTypeIds: d.linkedOperationTypeIds ?? [],
    linkedForestTypeIds: d.linkedForestTypeIds ?? [],
  };
}

export async function listInterventionTypes(): Promise<
  (InterventionType & { linkedForestTypeIds?: string[] })[]
> {
  const snapshot = await getDocs(
    query(collection(getDb(), INTERVENTION_TYPES), orderBy('order', 'asc'))
  );
  return snapshot.docs.map(toInterventionType);
}

export async function createInterventionType(data: Omit<InterventionType, 'id'>): Promise<string> {
  const ref = await addDoc(collection(getDb(), INTERVENTION_TYPES), data);
  return ref.id;
}

export async function updateInterventionType(
  id: string,
  data: Partial<Omit<InterventionType, 'id'>>
): Promise<void> {
  await updateDoc(doc(getDb(), INTERVENTION_TYPES, id), data);
}

export async function deleteInterventionType(id: string): Promise<void> {
  await deleteDoc(doc(getDb(), INTERVENTION_TYPES, id));
}

// ---------------------------------------------------------------------------
// Relationship cascade updates
// ---------------------------------------------------------------------------

/**
 * Updates an OperationType's linkedForestTypeIds and cascades to all
 * InterventionTypes and ForestryOperations that reference this OperationType.
 */
export async function updateOperationTypeLinks(
  otId: string,
  newLinkedForestTypeIds: string[],
  allOperationTypes: (OperationType & { linkedForestTypeIds?: string[] })[]
): Promise<void> {
  await updateDoc(doc(getDb(), OPERATION_TYPES, otId), {
    linkedForestTypeIds: newLinkedForestTypeIds,
  });

  // Build a map with the freshly-updated OT
  const otMap = new Map<string, string[]>(
    allOperationTypes.map(ot => [
      ot.id,
      ot.id === otId ? newLinkedForestTypeIds : (ot.linkedForestTypeIds ?? []),
    ])
  );
  if (!otMap.has(otId)) otMap.set(otId, newLinkedForestTypeIds);

  const [itSnap, foSnap] = await Promise.all([
    getDocs(
      query(
        collection(getDb(), INTERVENTION_TYPES),
        where('linkedOperationTypeIds', 'array-contains', otId)
      )
    ),
    getDocs(
      query(
        collection(getDb(), FORESTRY_OPS),
        where('linkedOperationTypeIds', 'array-contains', otId)
      )
    ),
  ]);

  const allDocs = [...itSnap.docs, ...foSnap.docs];
  if (allDocs.length === 0) return;

  for (let i = 0; i < allDocs.length; i += BATCH_MAX) {
    const batch = writeBatch(getDb());
    allDocs.slice(i, i + BATCH_MAX).forEach(d => {
      const linkedOtIds: string[] = d.data().linkedOperationTypeIds ?? [];
      const linkedForestTypeIds = [...new Set(linkedOtIds.flatMap(id => otMap.get(id) ?? []))];
      batch.update(d.ref, { linkedForestTypeIds });
    });
    await batch.commit();
  }
}

/**
 * Updates an InterventionType's linkedOperationTypeIds (and derives linkedForestTypeIds),
 * then cascades to all ForestryOperations that reference this InterventionType.
 */
export async function updateInterventionTypeLinks(
  itId: string,
  newLinkedOperationTypeIds: string[],
  allOperationTypes: (OperationType & { linkedForestTypeIds?: string[] })[]
): Promise<void> {
  const otMap = new Map<string, string[]>(
    allOperationTypes.map(ot => [ot.id, ot.linkedForestTypeIds ?? []])
  );

  const derivedForestTypeIds = [
    ...new Set(newLinkedOperationTypeIds.flatMap(id => otMap.get(id) ?? [])),
  ];

  await updateDoc(doc(getDb(), INTERVENTION_TYPES, itId), {
    linkedOperationTypeIds: newLinkedOperationTypeIds,
    linkedForestTypeIds: derivedForestTypeIds,
  });

  const foSnap = await getDocs(
    query(
      collection(getDb(), FORESTRY_OPS),
      where('linkedInterventionTypeIds', 'array-contains', itId)
    )
  );
  if (foSnap.empty) return;

  // Fetch all intervention types to rebuild derived IDs per operation
  const allItSnap = await getDocs(collection(getDb(), INTERVENTION_TYPES));
  const itOtMap = new Map<string, string[]>();
  for (const d of allItSnap.docs) {
    itOtMap.set(
      d.id,
      d.id === itId
        ? newLinkedOperationTypeIds
        : ((d.data().linkedOperationTypeIds as string[]) ?? [])
    );
  }

  for (let i = 0; i < foSnap.docs.length; i += BATCH_MAX) {
    const batch = writeBatch(getDb());
    foSnap.docs.slice(i, i + BATCH_MAX).forEach(d => {
      const linkedItIds: string[] = d.data().linkedInterventionTypeIds ?? [];
      const derivedOtIds = [...new Set(linkedItIds.flatMap(id => itOtMap.get(id) ?? []))];
      const derivedFtIds = [...new Set(derivedOtIds.flatMap(id => otMap.get(id) ?? []))];
      batch.update(d.ref, {
        linkedOperationTypeIds: derivedOtIds,
        linkedForestTypeIds: derivedFtIds,
      });
    });
    await batch.commit();
  }
}

/**
 * Derives linkedOperationTypeIds and linkedForestTypeIds for an operation
 * based on the selected intervention types.
 * Returns the derived IDs to be included when creating/updating an operation.
 */
export function deriveOperationLinkedIds(
  linkedInterventionTypeIds: string[],
  interventionTypes: (InterventionType & { linkedOperationTypeIds?: string[] })[],
  operationTypes: (OperationType & { linkedForestTypeIds?: string[] })[]
): { linkedOperationTypeIds: string[]; linkedForestTypeIds: string[] } {
  const linkedOperationTypeIds = [
    ...new Set(
      linkedInterventionTypeIds.flatMap(
        itId => interventionTypes.find(it => it.id === itId)?.linkedOperationTypeIds ?? []
      )
    ),
  ];
  const linkedForestTypeIds = [
    ...new Set(
      linkedOperationTypeIds.flatMap(
        otId => operationTypes.find(ot => ot.id === otId)?.linkedForestTypeIds ?? []
      )
    ),
  ];
  return { linkedOperationTypeIds, linkedForestTypeIds };
}

// ---------------------------------------------------------------------------
// Excel import (replace-all)
// ---------------------------------------------------------------------------

const BATCH_MAX = 499;

async function clearFirestoreCollection(collectionName: string): Promise<void> {
  const snapshot = await getDocs(collection(getDb(), collectionName));
  for (let i = 0; i < snapshot.docs.length; i += BATCH_MAX) {
    const batch = writeBatch(getDb());
    snapshot.docs.slice(i, i + BATCH_MAX).forEach(d => batch.delete(d.ref));
    await batch.commit();
  }
}

/**
 * Replace all forestry data in Firestore with the parsed contents of the uploaded
 * Excel file. Clears all 4 collections then batch-writes the new data.
 */
export async function importForestryFromExcel(data: ForestryImportData): Promise<void> {
  if (data.operations.length === 0) {
    throw new Error(
      'Parsed file contains 0 operations — import aborted to prevent data loss. ' +
        'Check that the file has the expected sheet names ("Opções", "Custos e Proveitos") ' +
        'and column structure.'
    );
  }

  await Promise.all([
    clearFirestoreCollection(FORESTRY_OPS),
    clearFirestoreCollection(FOREST_TYPES),
    clearFirestoreCollection(OPERATION_TYPES),
    clearFirestoreCollection(INTERVENTION_TYPES),
  ]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const writeInBatches = async (collectionName: string, items: any[]) => {
    for (let i = 0; i < items.length; i += BATCH_MAX) {
      const batch = writeBatch(getDb());
      items.slice(i, i + BATCH_MAX).forEach(item => {
        batch.set(doc(getDb(), collectionName, item.id), item);
      });
      await batch.commit();
    }
  };

  await writeInBatches(FOREST_TYPES, data.forestTypes);
  await writeInBatches(OPERATION_TYPES, data.operationTypes);
  await writeInBatches(INTERVENTION_TYPES, data.interventionTypes);
  await writeInBatches(FORESTRY_OPS, data.operations);
}
